import { useState, useEffect, useCallback } from 'react';
import * as authService from '../services/authService';
import * as databaseService from '../services/databaseService';

/**
 * Custom hook for authentication state management
 * Uses React 19 optimizations and proper error handling
 * 
 * PII (displayName, email, photoURL) is stored ONLY in client-side state.
 * Server-side user record contains only uid, isAdmin, and download tracking.
 */
export function useAuth() {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null); // Client-side PII only
    const [userRecord, setUserRecord] = useState(null); // Server-side data (uid, isAdmin, etc.)
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Memoized auth state handler
    const handleAuthStateChange = useCallback(async (firebaseUser) => {
        try {
            setError(null);

            if (firebaseUser) {
                // Extract PII for client-side storage only
                const profile = {
                    uid: firebaseUser.uid,
                    displayName: firebaseUser.displayName,
                    email: firebaseUser.email,
                    photoURL: firebaseUser.photoURL
                };

                // Sync user data to database (NO PII sent)
                await databaseService.syncUserData(firebaseUser);

                // Get user record from database (uid, isAdmin, download tracking)
                const userData = await databaseService.getUserData(firebaseUser.uid);

                setUser(firebaseUser);
                setUserProfile(profile); // Store PII client-side only
                setUserRecord(userData);
            } else {
                setUser(null);
                setUserProfile(null); // Clear PII on logout
                setUserRecord(null);
            }
        } catch (err) {
            console.error('Auth state change error:', err);
            setError(err.message);
            setUser(null);
            setUserProfile(null);
            setUserRecord(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const unsubscribe = authService.onAuthStateChanged(handleAuthStateChange);

        return () => {
            unsubscribe();
        };
    }, [handleAuthStateChange]);

    // Real-time user record updates
    useEffect(() => {
        let unsubscribeUser = null;

        if (user?.uid) {
            unsubscribeUser = databaseService.listenToUser(user.uid, (userData) => {
                setUserRecord(userData);
            });
        }

        return () => {
            if (unsubscribeUser) {
                unsubscribeUser();
            }
        };
    }, [user?.uid]);

    const signOut = useCallback(async () => {
        try {
            setError(null);
            // Clear PII from state before signing out
            setUserProfile(null);
            await authService.signOut();
        } catch (err) {
            console.error('Sign out error:', err);
            setError(err.message);
            throw err;
        }
    }, []);

    const signInWithGoogle = useCallback(async () => {
        try {
            setError(null);
            setLoading(true);
            const result = await authService.signInWithGoogle();
            return result;
        } catch (err) {
            console.error('Sign in error:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        user,
        userProfile, // Client-side PII (displayName, email, photoURL)
        userRecord, // Server-side data (uid, isAdmin, downloadCount, etc.)
        loading,
        error,
        signOut,
        signInWithGoogle,
        isAuthenticated: !!user,
        isAdmin: !!userRecord?.isAdmin,
    };
}